import { useEffect, useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, Cloud, Download, RefreshCw, Search } from "lucide-react";
import { api, type ApiCategory, type VodItem } from "@/lib/api";

function fmtDuration(sec: number) {
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function bestPlayUrl(it: VodItem): string {
  // 优先转码后的 mp4，其次原始上传
  const mp4s = it.playUrls.filter((p) => p.url.endsWith(".mp4"));
  if (mp4s.length > 0) {
    // 选最高码率的 mp4
    return mp4s.sort((a, b) => b.bitrate - a.bitrate)[0].url;
  }
  return it.sourceUrl;
}

interface Selection {
  selected: boolean;
  title: string;
  category_id: number;
  url: string;
  description: string;
}

export default function AdminVodImportTab() {
  const [items, setItems] = useState<VodItem[]>([]);
  const [cats, setCats] = useState<ApiCategory[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [selections, setSelections] = useState<Record<string, Selection>>({});
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<
    Array<{ fileId: string; ok: boolean; error?: string }> | null
  >(null);
  const pageSize = 50;

  async function load(p = page) {
    setLoading(true);
    setErr(null);
    try {
      const [v, c] = await Promise.all([
        api.vodList(p, pageSize, search || undefined),
        cats.length ? Promise.resolve({ categories: cats }) : api.listCategories(),
      ]);
      setItems(v.items);
      setTotal(v.total);
      setCats(c.categories);
      setPage(v.page);
      // 初始化选择
      setSelections((prev) => {
        const next = { ...prev };
        for (const it of v.items) {
          if (!next[it.fileId]) {
            next[it.fileId] = {
              selected: false,
              title: stripExt(it.name),
              category_id: c.categories[0]?.id || 0,
              url: bestPlayUrl(it),
              description: it.description || "",
            };
          }
        }
        return next;
      });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(1); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  function stripExt(name: string) {
    return name.replace(/\.(mov|mp4|m3u8|webm)$/i, "");
  }

  function update(fileId: string, patch: Partial<Selection>) {
    setSelections((prev) => ({ ...prev, [fileId]: { ...prev[fileId], ...patch } }));
  }

  function selectAll(on: boolean) {
    setSelections((prev) => {
      const next = { ...prev };
      for (const it of items) {
        if (!it.imported) next[it.fileId] = { ...next[it.fileId], selected: on };
      }
      return next;
    });
  }

  const selectedCount = useMemo(
    () => items.filter((it) => !it.imported && selections[it.fileId]?.selected).length,
    [items, selections]
  );

  async function doImport() {
    if (cats.length === 0) {
      alert("请先到「分类」标签页创建至少一个分类");
      return;
    }
    const toImport = items
      .filter((it) => !it.imported && selections[it.fileId]?.selected)
      .map((it) => {
        const s = selections[it.fileId];
        return {
          fileId: it.fileId,
          title: s.title.trim(),
          vod_url: s.url,
          cover_url: it.cover || "",
          category_id: s.category_id,
          description: s.description.trim() || undefined,
        };
      });
    if (toImport.length === 0) return;
    if (!confirm(`确认导入 ${toImport.length} 个视频？`)) return;
    setImporting(true);
    setImportResult(null);
    try {
      const r = await api.vodImport(toImport);
      setImportResult(r.results);
      // 重新加载列表，imported 状态会更新
      await load(page);
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setImporting(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <div className="card mb-6 p-5">
        <div className="flex items-center gap-3">
          <Cloud className="h-5 w-5 text-brand" />
          <div>
            <div className="text-sm font-semibold text-ink-900">从腾讯 VOD 导入</div>
            <p className="text-[11px] text-ink-400">
              自动列出腾讯云 VOD 中的视频。勾选 → 给每个挑分类 → 一键导入。已导入的会标记。
            </p>
          </div>
          <div className="flex-1" />
          <button onClick={() => load(page)} className="btn-ghost" disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            刷新
          </button>
        </div>
        <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
            <input
              className="input pl-9"
              placeholder="按视频名称搜索…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(1); }}
            />
          </div>
          <button onClick={() => load(1)} className="btn-ghost">搜索</button>
          <div className="text-xs text-ink-400">
            共 <strong className="text-ink-900">{total}</strong> 个视频
          </div>
        </div>
      </div>

      {err && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4" /> {err}
        </div>
      )}

      {/* 批量操作栏 */}
      <div className="sticky top-2 z-10 mb-4 flex items-center justify-between rounded-lg border border-surface-border bg-white px-4 py-2 shadow-card">
        <label className="flex items-center gap-2 text-sm text-ink-600">
          <input
            type="checkbox"
            onChange={(e) => selectAll(e.target.checked)}
            checked={items.length > 0 && items.every((it) => it.imported || selections[it.fileId]?.selected)}
          />
          全选本页（已导入的会跳过）
        </label>
        <div className="flex items-center gap-3 text-xs text-ink-400">
          <span>已选 <strong className="text-ink-900">{selectedCount}</strong></span>
          <button
            onClick={doImport}
            disabled={selectedCount === 0 || importing}
            className="btn-primary !py-1.5 !px-4"
          >
            <Download className="h-3.5 w-3.5" />
            {importing ? "导入中…" : `导入选中 ${selectedCount} 个`}
          </button>
        </div>
      </div>

      {importResult && (
        <div className="mb-4 rounded-lg border border-surface-border bg-white p-4">
          <div className="text-sm font-semibold text-ink-900 mb-2">导入结果</div>
          <ul className="space-y-1 text-xs">
            {importResult.map((r) => (
              <li key={r.fileId} className={r.ok ? "text-green-600" : "text-red-600"}>
                {r.ok ? "✓" : "✗"} {r.fileId.slice(0, 12)}… {r.ok ? "成功" : `失败：${r.error}`}
              </li>
            ))}
          </ul>
        </div>
      )}

      {loading ? (
        <div className="py-20 text-center text-sm text-ink-400">加载中…</div>
      ) : items.length === 0 ? (
        <div className="py-20 text-center text-sm text-ink-400">无视频</div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => {
            const s = selections[it.fileId];
            if (!s) return null;
            return (
              <div
                key={it.fileId}
                className={`rounded-lg border bg-white p-4 ${
                  it.imported
                    ? "border-surface-border opacity-60"
                    : s.selected
                      ? "border-brand ring-1 ring-brand/30"
                      : "border-surface-border"
                }`}
              >
                <div className="flex gap-4">
                  <input
                    type="checkbox"
                    className="mt-1.5 h-4 w-4"
                    checked={s.selected && !it.imported}
                    disabled={it.imported}
                    onChange={(e) => update(it.fileId, { selected: e.target.checked })}
                  />
                  {it.cover ? (
                    <img src={it.cover} alt="" className="h-20 w-32 shrink-0 rounded-md object-cover bg-ink-900" loading="lazy" />
                  ) : (
                    <div className="h-20 w-32 shrink-0 rounded-md bg-ink-900" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-ink-400 font-mono">{(it.fileId || "").slice(0, 12)}…</span>
                      {it.imported && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-semibold text-green-600">
                          <CheckCircle2 className="h-3 w-3" /> 已导入
                        </span>
                      )}
                      <span className="text-[11px] text-ink-400">
                        · {fmtDuration(it.durationSec)} · {it.sizeMB} MB
                      </span>
                    </div>
                    <div className="mt-2 grid grid-cols-1 gap-3 lg:grid-cols-[1fr_180px_200px]">
                      <input
                        className="input !py-1.5 !text-sm"
                        placeholder="视频标题"
                        value={s.title}
                        disabled={it.imported}
                        onChange={(e) => update(it.fileId, { title: e.target.value })}
                      />
                      <select
                        className="input !py-1.5 !text-sm"
                        value={s.category_id}
                        disabled={it.imported}
                        onChange={(e) => update(it.fileId, { category_id: Number(e.target.value) })}
                      >
                        {cats.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                      <select
                        className="input !py-1.5 !text-xs font-mono"
                        value={s.url}
                        disabled={it.imported}
                        onChange={(e) => update(it.fileId, { url: e.target.value })}
                      >
                        {it.playUrls.map((p) => (
                          <option key={p.url} value={p.url}>
                            {p.url.endsWith(".mp4") ? "MP4" : "原始"} · {Math.round(p.bitrate / 1000)}k
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <button
            onClick={() => load(page - 1)}
            disabled={page <= 1 || loading}
            className="btn-ghost !py-1.5 !px-3 text-sm"
          >
            上一页
          </button>
          <span className="text-xs text-ink-400">
            第 {page} / {totalPages} 页
          </span>
          <button
            onClick={() => load(page + 1)}
            disabled={page >= totalPages || loading}
            className="btn-ghost !py-1.5 !px-3 text-sm"
          >
            下一页
          </button>
        </div>
      )}
    </>
  );
}
